import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Plus, Edit, Trash2, ChevronRight, ChevronDown, FolderTree, Folder, File } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCategories() {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('categories');
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Dialog states
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [isSubcategoryDialogOpen, setIsSubcategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  
  // Form data
  const [categoryForm, setCategoryForm] = useState({
    name: '', slug: '', description: '', image_url: '', display_order: 0
  });
  const [subcategoryForm, setSubcategoryForm] = useState({
    name: '', slug: '', category_id: '', description: '', image_url: '', display_order: 0
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchData = async () => {
    try {
      const [catRes, subRes, menuRes] = await Promise.all([
        axios.get(`${API}/categories/all`, { headers }),
        axios.get(`${API}/subcategories/all`, { headers }),
        axios.get(`${API}/menu/all`, { headers })
      ]);
      setCategories(catRes.data);
      setSubcategories(subRes.data);
      setMenuItems(menuRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  // Category handlers
  const openCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        slug: category.slug,
        description: category.description || '',
        image_url: category.image_url || '',
        display_order: category.display_order || 0
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', slug: '', description: '', image_url: '', display_order: categories.length });
    }
    setIsCategoryDialogOpen(true);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, categoryForm, { headers });
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/categories`, categoryForm, { headers });
        toast.success('Category created');
      }
      setIsCategoryDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Failed to save category');
    }
  };

  const deleteCategory = async (id) => {
    if (!confirm('Delete this category? All subcategories will also be deactivated.')) return;
    try {
      await axios.delete(`${API}/categories/${id}`, { headers });
      toast.success('Category deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };

  // Subcategory handlers
  const openSubcategoryDialog = (subcategory = null, categoryId = null) => {
    if (subcategory) {
      setEditingSubcategory(subcategory);
      setSubcategoryForm({
        name: subcategory.name,
        slug: subcategory.slug,
        category_id: subcategory.category_id,
        description: subcategory.description || '',
        image_url: subcategory.image_url || '',
        display_order: subcategory.display_order || 0
      });
    } else {
      setEditingSubcategory(null);
      setSubcategoryForm({
        name: '', slug: '', category_id: categoryId || categories[0]?.id || '',
        description: '', image_url: '', display_order: 0
      });
    }
    setIsSubcategoryDialogOpen(true);
  };

  const handleSubcategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSubcategory) {
        await axios.put(`${API}/subcategories/${editingSubcategory.id}`, subcategoryForm, { headers });
        toast.success('Subcategory updated');
      } else {
        await axios.post(`${API}/subcategories`, subcategoryForm, { headers });
        toast.success('Subcategory created');
      }
      setIsSubcategoryDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving subcategory:', error);
      toast.error('Failed to save subcategory');
    }
  };

  const deleteSubcategory = async (id) => {
    if (!confirm('Delete this subcategory?')) return;
    try {
      await axios.delete(`${API}/subcategories/${id}`, { headers });
      toast.success('Subcategory deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      toast.error('Failed to delete subcategory');
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const getSubcategoriesForCategory = (categoryId) => {
    return subcategories.filter(s => s.category_id === categoryId);
  };

  const getItemsForCategory = (categoryId) => {
    return menuItems.filter(i => i.category_id === categoryId && !i.subcategory_id);
  };

  const getItemsForSubcategory = (subcategoryId) => {
    return menuItems.filter(i => i.subcategory_id === subcategoryId);
  };

  if (loading) {
    return (
      <AdminLayout title="Categories" subtitle="Manage menu categories and subcategories.">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-fire-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Categories" subtitle="Manage menu categories and subcategories for hierarchical menu.">
      <div data-testid="admin-categories">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <div className="flex justify-between items-center mb-6">
            <TabsList className="bg-card border border-white/10">
              <TabsTrigger value="categories" className="data-[state=active]:bg-fire-500">
                <FolderTree className="w-4 h-4 mr-2" /> Hierarchy View
              </TabsTrigger>
              <TabsTrigger value="list" className="data-[state=active]:bg-fire-500">
                <Folder className="w-4 h-4 mr-2" /> List View
              </TabsTrigger>
            </TabsList>
            
            <div className="flex gap-2">
              <Button onClick={() => openCategoryDialog()} className="bg-fire-500 hover:bg-fire-600" data-testid="add-category-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Category
              </Button>
              <Button onClick={() => openSubcategoryDialog()} variant="outline" className="border-white/10 text-white" data-testid="add-subcategory-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Subcategory
              </Button>
            </div>
          </div>

        {/* Hierarchy View */}
        <TabsContent value="categories" className="mt-0">
          <div className="space-y-2">
            {categories.filter(c => c.is_active).map(category => (
              <div key={category.id} className="bg-card rounded-xl border border-white/10 overflow-hidden">
                <Collapsible open={expandedCategories[category.id]} onOpenChange={() => toggleCategory(category.id)}>
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center gap-4 p-4 cursor-pointer hover:bg-white/5">
                      {expandedCategories[category.id] ? (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      )}
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-black/50">
                        {category.image_url && <img src={category.image_url} alt={category.name} className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-white font-bold text-lg">{category.name}</h3>
                        <p className="text-gray-500 text-sm">
                          {getSubcategoriesForCategory(category.id).length} subcategories • {getItemsForCategory(category.id).length} direct items
                        </p>
                      </div>
                      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" onClick={() => openSubcategoryDialog(null, category.id)} className="text-gray-400 hover:text-white">
                          <Plus className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openCategoryDialog(category)} className="text-gray-400 hover:text-white">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteCategory(category.id)} className="text-red-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <div className="border-t border-white/5">
                      {/* Subcategories */}
                      {getSubcategoriesForCategory(category.id).map(subcategory => (
                        <div key={subcategory.id} className="border-b border-white/5 last:border-b-0">
                          <div className="flex items-center gap-4 p-4 pl-16 hover:bg-white/5">
                            <Folder className="w-5 h-5 text-fire-500" />
                            <div className="flex-1">
                              <h4 className="text-white font-medium">{subcategory.name}</h4>
                              <p className="text-gray-500 text-sm">{getItemsForSubcategory(subcategory.id).length} items</p>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" variant="ghost" onClick={() => openSubcategoryDialog(subcategory)} className="text-gray-400 hover:text-white">
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => deleteSubcategory(subcategory.id)} className="text-red-400 hover:text-red-500">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          {/* Items under subcategory */}
                          {getItemsForSubcategory(subcategory.id).map(item => (
                            <div key={item.id} className="flex items-center gap-4 p-3 pl-24 text-gray-400 text-sm hover:bg-white/5">
                              <File className="w-4 h-4" />
                              <span className="flex-1">{item.name}</span>
                              <span className="text-fire-500">₦{item.price.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                      
                      {/* Direct items (no subcategory) */}
                      {getItemsForCategory(category.id).length > 0 && (
                        <div className="border-t border-white/10 p-4 pl-16">
                          <p className="text-gray-500 text-sm mb-2">Direct items (no subcategory):</p>
                          {getItemsForCategory(category.id).map(item => (
                            <div key={item.id} className="flex items-center gap-4 py-2 text-gray-400 text-sm">
                              <File className="w-4 h-4" />
                              <span className="flex-1">{item.name}</span>
                              <span className="text-fire-500">₦{item.price.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* List View */}
        <TabsContent value="list" className="mt-0">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Categories List */}
            <div className="bg-card rounded-xl border border-white/10 p-6">
              <h3 className="text-white font-bold text-lg mb-4">Categories</h3>
              <div className="space-y-3">
                {categories.map(category => (
                  <div key={category.id} className={`flex items-center gap-3 p-3 rounded-lg ${category.is_active ? 'bg-black/50' : 'bg-black/20 opacity-50'}`}>
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-black">
                      {category.image_url && <img src={category.image_url} alt={category.name} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{category.name}</p>
                      <p className="text-gray-500 text-xs">{category.slug}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openCategoryDialog(category)} className="text-gray-400 hover:text-white p-1">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteCategory(category.id)} className="text-red-400 hover:text-red-500 p-1">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Subcategories List */}
            <div className="bg-card rounded-xl border border-white/10 p-6">
              <h3 className="text-white font-bold text-lg mb-4">Subcategories</h3>
              <div className="space-y-3">
                {subcategories.map(subcategory => {
                  const parentCategory = categories.find(c => c.id === subcategory.category_id);
                  return (
                    <div key={subcategory.id} className={`flex items-center gap-3 p-3 rounded-lg ${subcategory.is_active ? 'bg-black/50' : 'bg-black/20 opacity-50'}`}>
                      <Folder className="w-5 h-5 text-fire-500" />
                      <div className="flex-1">
                        <p className="text-white font-medium">{subcategory.name}</p>
                        <p className="text-gray-500 text-xs">Under: {parentCategory?.name || 'Unknown'}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openSubcategoryDialog(subcategory)} className="text-gray-400 hover:text-white p-1">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteSubcategory(subcategory.id)} className="text-red-400 hover:text-red-500 p-1">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </TabsContent>
        </Tabs>

        {/* Category Dialog */}
        <Dialog open={isCategoryDialogOpen} onOpenChange={setIsCategoryDialogOpen}>
          <DialogContent className="bg-card border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingCategory ? 'Edit Category' : 'Add Category'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleCategorySubmit} className="space-y-4">
              <div>
                <Label className="text-gray-400">Name</Label>
                <Input
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({
                    ...categoryForm,
                    name: e.target.value,
                    slug: generateSlug(e.target.value)
                  })}
                  required
                  className="bg-black/50 border-white/10 text-white"
                  data-testid="category-form-name"
                />
              </div>
              
              <div>
                <Label className="text-gray-400">Slug</Label>
                <Input
                  value={categoryForm.slug}
                  onChange={(e) => setCategoryForm({ ...categoryForm, slug: e.target.value })}
                  required
                  className="bg-black/50 border-white/10 text-white"
                />
              </div>
              
              <div>
                <Label className="text-gray-400">Description</Label>
                <Input
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  className="bg-black/50 border-white/10 text-white"
                />
              </div>
              
              <div>
                <Label className="text-gray-400">Image URL</Label>
                <Input
                  value={categoryForm.image_url}
                  onChange={(e) => setCategoryForm({ ...categoryForm, image_url: e.target.value })}
                  className="bg-black/50 border-white/10 text-white"
                  placeholder="https://..."
                />
              </div>
              
              <div>
                <Label className="text-gray-400">Display Order</Label>
                <Input
                  type="number"
                  value={categoryForm.display_order}
                  onChange={(e) => setCategoryForm({ ...categoryForm, display_order: parseInt(e.target.value) || 0 })}
                  className="bg-black/50 border-white/10 text-white"
                />
              </div>
              
              <div className="flex gap-4 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsCategoryDialogOpen(false)} className="flex-1 border-white/10 text-white">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-fire-500 hover:bg-fire-600" data-testid="category-form-submit">
                  {editingCategory ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Subcategory Dialog */}
        <Dialog open={isSubcategoryDialogOpen} onOpenChange={setIsSubcategoryDialogOpen}>
          <DialogContent className="bg-card border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingSubcategory ? 'Edit Subcategory' : 'Add Subcategory'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubcategorySubmit} className="space-y-4">
              <div>
                <Label className="text-gray-400">Parent Category</Label>
                <Select value={subcategoryForm.category_id} onValueChange={(v) => setSubcategoryForm({ ...subcategoryForm, category_id: v })}>
                  <SelectTrigger className="bg-black/50 border-white/10 text-white">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    {categories.filter(c => c.is_active).map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-gray-400">Name</Label>
                <Input
                  value={subcategoryForm.name}
                  onChange={(e) => setSubcategoryForm({
                    ...subcategoryForm,
                    name: e.target.value,
                    slug: generateSlug(e.target.value)
                  })}
                  required
                  className="bg-black/50 border-white/10 text-white"
                  data-testid="subcategory-form-name"
                />
              </div>
              
              <div>
                <Label className="text-gray-400">Slug</Label>
                <Input
                  value={subcategoryForm.slug}
                  onChange={(e) => setSubcategoryForm({ ...subcategoryForm, slug: e.target.value })}
                  required
                  className="bg-black/50 border-white/10 text-white"
                />
              </div>
              
              <div>
                <Label className="text-gray-400">Description</Label>
                <Input
                  value={subcategoryForm.description}
                  onChange={(e) => setSubcategoryForm({ ...subcategoryForm, description: e.target.value })}
                  className="bg-black/50 border-white/10 text-white"
                />
              </div>
              
              <div>
                <Label className="text-gray-400">Image URL</Label>
                <Input
                  value={subcategoryForm.image_url}
                  onChange={(e) => setSubcategoryForm({ ...subcategoryForm, image_url: e.target.value })}
                  className="bg-black/50 border-white/10 text-white"
                  placeholder="https://..."
                />
              </div>
              
              <div className="flex gap-4 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsSubcategoryDialogOpen(false)} className="flex-1 border-white/10 text-white">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-fire-500 hover:bg-fire-600" data-testid="subcategory-form-submit">
                  {editingSubcategory ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
